<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import PageLayout from '../Components/UI/PageLayout.vue'
import MainContainer from '../Components/UI/Content/MainContainer.vue'
import BlockContent from '../Components/UI/Content/BlockContent.vue'
import TableMenu from '../Components/UI/Table/TableMenu.vue'
import CardContent from '../Components/UI/Content/CardContent.vue'
import DataTable from '../Components/UI/Table/DataTable.vue'
import ModalError from '../Components/UI/Modal/ModalError.vue'
import { getContractListColumns } from '../Constants/ContractColumns'
import useApi from '../Composables/useApi'
import useUser from '../../../Composables/User/useUser.ts'
import useSearchParams from '../Composables/useSearchParams'
import useContractExport from '../Composables/useContractExport'
import useConfig from '../../../Composables/useConfig.ts'

const { t } = useI18n()
const { getJSON } = useApi()
const { user } = useUser()
const { searchParams } = useSearchParams()
const { exportContracts, canDownloadExport } = useContractExport()
const { isTheme } = useConfig()
const selectedStatus = ref(parseInt(searchParams.get('s') ?? 0))
const errors = ref([])
const contractList = ref(null)
const contractListRequest = ref(null)

const filters = ref({
  units: [],
  platformSalesmen: [],
  suppliers: [],
  clients: [],
  refinancers: [],
  purchaseOrderNumber: null,
})

const filtersValues = ref({
  units: null,
  platformSalesman: null,
  supplier: null,
  client: null,
  refinancer: null,
  purchaseOrderNumber: null,
})

const showDownloadExportButton = computed(() => {
  return canDownloadExport(user.value?.roles || [])
})

const submenuLabels = computed(() => [
  { id: 0, name: t('contract.list.type.status.ALL') },
  { id: 1, name: t('contract.list.type.status.CREATED') },
  { id: 2, name: t('contract.list.type.status.PENDING') },
  { id: 3, name: t('contract.list.type.status.CLIENT_SIGNED') },
  { id: 4, name: t('contract.list.type.status.OTHER') },
])

const tableFilters = computed(() => {
  return [
    { name: 'client', optionsKey: 'clients', domain: 'simulation', labelTop: true, condition: true },
    { name: 'supplier', optionsKey: 'suppliers', domain: 'simulation', labelTop: true, condition: true },
    { name: 'unit', optionsKey: 'units', domain: 'simulation', labelTop: true, condition: true },
    { name: 'platformSalesman', optionsKey: 'platformSalesmen', domain: 'simulation', labelTop: true, condition: true },
    {
      name: 'refinancer',
      optionsKey: 'refinancers',
      domain: 'simulation',
      labelTop: true,
      condition: filters.value.refinancers.length > 1,
    },
    {
      name: 'purchaseOrderNumber',
      domain: 'simulation',
      labelTop: true,
      condition: isTheme('ciscar'),
    },
  ].filter(({ condition }) => condition === true)
})

const columns = computed(() => {
  return getContractListColumns(t)
})

const loadFilterOptions = async () => {
  try {
    const options = await getJSON(`contract/filter-options?${searchParams.toString()}`)
    filters.value = options
  } catch {
    errors.value.push(t('error.loadingFilterOptions'))
  }
}

const updateFilterData = async (data) => {
  filtersValues.value = { ...filtersValues.value, ...data }
  cancelPendingRequest()

  for (const [name, value] of Object.entries(data)) {
    if (value && (typeof value === 'string' || value > 0)) {
      searchParams.set(name, value)
    } else {
      searchParams.delete(name)
    }
  }

  contractList.value?.renderDataTable()
  await loadFilterOptions()
}

const setDateTable = async (createdAtValue, createdAtEndValue) => {
  cancelPendingRequest()
  if (createdAtValue) {
    searchParams.set('createdAt', createdAtValue)
  } else {
    searchParams.delete('createdAt')
  }
  if (createdAtEndValue) {
    searchParams.set('createdAtEnd', createdAtEndValue)
  } else {
    searchParams.delete('createdAtEnd')
  }
  contractList.value?.renderDataTable()
  await loadFilterOptions()
}

const setSelectedStatus = (value) => {
  value = parseInt(value)
  if (value !== selectedStatus.value) {
    selectedStatus.value = value
    updateParams()
  }
}

const updateParams = async () => {
  cancelPendingRequest()
  let statuses = []
  switch (selectedStatus.value) {
    case 1:
      statuses.push('CREATED')
      break
    case 2:
      statuses.push('VALIDATED', 'STUDY', 'STUDIED', 'PENDING')
      break
    case 3:
      statuses.push('CLIENT_SIGNED', 'COMPANY_SIGNED', 'CHECKED', 'ORDERED', 'WAITING')
      break
    case 4:
      statuses.push('DELIVERED', 'COMPLETE')
      break
    default:
      statuses.push(
        'CREATED',
        'VALIDATED',
        'STUDY',
        'STUDIED',
        'PENDING',
        'CLIENT_SIGNED',
        'COMPANY_SIGNED',
        'CHECKED',
        'ORDERED',
        'WAITING',
        'DELIVERED',
        'COMPLETE',
      )
  }
  searchParams.set('s', selectedStatus.value)
  searchParams.set('status', statuses.join('-'))
  contractList.value?.renderDataTable()
  await loadFilterOptions()
}

const cancelPendingRequest = () => {
  contractListRequest.value?.cancel()
}

const getContracts = async (callback, { page, limit, search, sort = [] }) => {
  cancelPendingRequest()

  const newUrlSearchParams = new URLSearchParams(searchParams.toString())

  newUrlSearchParams.set('page', page.toString())
  newUrlSearchParams.set('limit', limit.toString())

  for (const { field, order } of sort) {
    newUrlSearchParams.set(`sort[${field}]`, order)
  }

  if (search) {
    newUrlSearchParams.set('q', search)
  }

  searchParams.value = newUrlSearchParams

  const getJsonPromise = getJSON(`v2/contracts?${newUrlSearchParams.toString()}`)
  contractListRequest.value = getJsonPromise
  const { items: contracts, total } = await getJsonPromise
  callback({ data: contracts, recordsTotal: total, recordsFiltered: total })
}

const download = async () => {
  await exportContracts('contracts/export', searchParams)
}

onMounted(async () => {
  await updateParams()
  await loadFilterOptions()
})
</script>
<template>
  <PageLayout menu="contrats-index">
    <MainContainer title-t="menu.myContracts">
      <BlockContent>
        <TableMenu
          :status="selectedStatus"
          :filters="tableFilters"
          :filter-options="filters"
          :filter-values="filtersValues"
          :submenu-labels="submenuLabels"
          :show-download-button="showDownloadExportButton"
          endpoint="contract"
          @set-selected-status="setSelectedStatus"
          @download="download"
          @set-date="setDateTable"
          @update-filter-data="updateFilterData"
        />
        <CardContent>
          <DataTable
            id="listeContrat"
            ref="contractList"
            :columns="columns"
            :fetch="getContracts"
            :order="[{ dir: 'desc', name: 'createdAt' }]"
            :zero-records="t('contract.list.zeroRecords')"
            :first-load="false"
            pagination-mode="server"
          />
        </CardContent>
      </BlockContent>
    </MainContainer>
    <ModalError v-if="errors.length > 0" id="modalError" ref="modalError" :errors="errors" />
  </PageLayout>
</template>
